import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelectionStore } from "../stores/selectionStore";
import { useMeshes } from "../stores/sceneStore";
import { getViewerActions, getViewerState } from "../stores/viewerStore";
import {
  MESH_INSPECTOR_WIREFRAME_OVERLAY_NAME,
  renderMeshWireframeOverlayForMeshNames,
} from "./wireframeOverlay";
import {
  clearMeshInspectorBoundingBoxOverlays,
  renderMeshInspectorBoundingBox,
  setMeshInspectorOrientationAxes,
} from "./inspectorOverlays";
import { logger } from "../utils/logger";
import type { MeshUUID } from "../types/primitives";

export type MeshVisibilityState = {
  focus: boolean;
  wireframe: boolean;
  boundingBox: boolean;
  originMarker: boolean;
  wireframeColor: string;
};

const defaultState = (): MeshVisibilityState => ({
  focus: false,
  wireframe: false,
  boundingBox: false,
  originMarker: false,
  wireframeColor: "#ff0000",
});

let rememberedMeshVisibilityState: MeshVisibilityState = defaultState();

function meshReferenceTransformSignature(mesh: unknown): string {
  const m = mesh as
    | {
        referencePosition?: unknown;
        referenceOrientation?: unknown;
      }
    | null
    | undefined;
  const pos = Array.isArray(m?.referencePosition) ? m.referencePosition : [0, 0, 0];
  const quat = Array.isArray(m?.referenceOrientation) ? m.referenceOrientation : [1, 0, 0, 0];
  return `${pos.map((value) => Number(value) || 0).join(",")}|${quat
    .map((value) => Number(value) || 0)
    .join(",")}`;
}

export type UseMeshVisibilityControlsArgs = {
  meshUuid: MeshUUID | null;
  bodyUuid?: string | null;
  enabled?: boolean;
  initialState?: Partial<MeshVisibilityState>;
  onStateChange?: (state: MeshVisibilityState) => void;
};

export type UseMeshVisibilityControlsResult = {
  state: MeshVisibilityState;
  disabled: boolean;
  focusDisabled: boolean;
  toggleFocus: () => void;
  toggleWireframe: () => void;
  toggleBoundingBox: () => void;
  toggleOriginMarker: () => void;
  setWireframeColor: (hex: string) => void;
  setState: (patch: Partial<MeshVisibilityState>) => void;
  clearAllOverlays: () => void;
  reset: () => void;
};

export function useMeshVisibilityControls({
  meshUuid,
  bodyUuid = null,
  enabled = true,
  initialState,
  onStateChange,
}: UseMeshVisibilityControlsArgs): UseMeshVisibilityControlsResult {
  const focusModeActive = useSelectionStore((s) => s.focusModeActive);
  const focusBodyName = useSelectionStore((s) => s.focusBodyName);
  const focusMeshUuid = useSelectionStore((s) => s.focusMeshUuid);
  const meshReferenceSignature = useMeshes((meshes) =>
    meshUuid ? meshReferenceTransformSignature(meshes?.[meshUuid]) : ""
  );
  const [sceneAssetsCompleteTick, setSceneAssetsCompleteTick] = useState(0);
  const [state, setStateInternal] = useState<MeshVisibilityState>(() => ({
    ...defaultState(),
    ...rememberedMeshVisibilityState,
    ...initialState,
  }));

  const disabled = !enabled || !meshUuid;
  const focusDisabled = disabled || !bodyUuid;

  const ensureVisualOn = useCallback(() => {
    if (!getViewerState().showVisual) {
      getViewerActions().toggleShowVisual();
    }
  }, []);

  const wireframeTargetRef = useRef<string | null>(null);
  const savedOpacityRef = useRef<number | null>(null);
  const prevOriginTargetRef = useRef<{ meshUuid: string; bodyUuid: string | null } | null>(null);
  const lastMeshUuidRef = useRef<string | null>(null);
  const lastBodyUuidRef = useRef<string | null>(null);
  const focusOwnedRef = useRef(false);
  lastMeshUuidRef.current = meshUuid;
  lastBodyUuidRef.current = bodyUuid;

  const setState = useCallback(
    (patch: Partial<MeshVisibilityState>) => {
      setStateInternal((prev) => {
        const next = { ...prev, ...patch };
        rememberedMeshVisibilityState = next;
        onStateChange?.(next);
        return next;
      });
    },
    [onStateChange]
  );

  const isFocusActiveForMesh = useMemo(
    () =>
      !!meshUuid &&
      !!bodyUuid &&
      !!focusModeActive &&
      focusMeshUuid === meshUuid &&
      focusBodyName === bodyUuid,
    [meshUuid, bodyUuid, focusModeActive, focusMeshUuid, focusBodyName]
  );

  const restoreWireframeOpacity = useCallback(() => {
    const target = wireframeTargetRef.current;
    const original = savedOpacityRef.current;
    if (target && original !== null) {
      getViewerActions().setMeshOpacity(target, original);
    }
    wireframeTargetRef.current = null;
    savedOpacityRef.current = null;
  }, []);

  useEffect(() => {
    if (!meshUuid) return;
    if (rememberedMeshVisibilityState.focus && bodyUuid && !isFocusActiveForMesh) {
      useSelectionStore.getState().setFocusMode(true, bodyUuid, meshUuid);
    }
    const nextFocus = rememberedMeshVisibilityState.focus || isFocusActiveForMesh;
    setStateInternal((prev) => (prev.focus === nextFocus ? prev : { ...prev, focus: nextFocus }));
  }, [meshUuid, bodyUuid, isFocusActiveForMesh]);

  useEffect(() => {
    if (disabled || !meshUuid || !state.wireframe) {
      renderMeshWireframeOverlayForMeshNames([], {
        visible: false,
        color: state.wireframeColor,
        overlayName: MESH_INSPECTOR_WIREFRAME_OVERLAY_NAME,
      });
      restoreWireframeOpacity();
      return;
    }

    if (wireframeTargetRef.current && wireframeTargetRef.current !== meshUuid) {
      restoreWireframeOpacity();
    }
    if (wireframeTargetRef.current !== meshUuid) {
      const opacityMap = getViewerState().meshOpacity || {};
      savedOpacityRef.current = opacityMap[meshUuid] ?? 1.0;
      wireframeTargetRef.current = meshUuid;
    }
    const currentOpacity = getViewerState().meshOpacity?.[meshUuid] ?? 1.0;
    if (currentOpacity !== 0.3) {
      getViewerActions().setMeshOpacity(meshUuid, 0.3);
    }
    renderMeshWireframeOverlayForMeshNames([meshUuid], {
      visible: true,
      color: state.wireframeColor,
      overlayName: MESH_INSPECTOR_WIREFRAME_OVERLAY_NAME,
    });
  }, [disabled, meshUuid, state.wireframe, state.wireframeColor, restoreWireframeOpacity]);

  useEffect(() => {
    if (disabled || !meshUuid || !state.boundingBox) {
      clearMeshInspectorBoundingBoxOverlays();
      return;
    }
    renderMeshInspectorBoundingBox(meshUuid, bodyUuid);
  }, [
    disabled,
    meshUuid,
    bodyUuid,
    state.boundingBox,
    meshReferenceSignature,
    sceneAssetsCompleteTick,
  ]);

  useEffect(() => {
    const prev = prevOriginTargetRef.current;
    const nextBodyUuid = bodyUuid ?? null;
    if (prev && (prev.meshUuid !== meshUuid || prev.bodyUuid !== nextBodyUuid)) {
      setMeshInspectorOrientationAxes(prev.meshUuid, false, prev.bodyUuid);
    }

    if (disabled || !meshUuid || !state.originMarker) {
      if (meshUuid) setMeshInspectorOrientationAxes(meshUuid, false, bodyUuid);
      prevOriginTargetRef.current = meshUuid ? { meshUuid, bodyUuid: nextBodyUuid } : null;
      return;
    }
    setMeshInspectorOrientationAxes(meshUuid, true, bodyUuid);
    prevOriginTargetRef.current = { meshUuid, bodyUuid: nextBodyUuid };
  }, [
    disabled,
    meshUuid,
    bodyUuid,
    state.originMarker,
    meshReferenceSignature,
    sceneAssetsCompleteTick,
  ]);

  useEffect(() => {
    if (!meshUuid || (!state.boundingBox && !state.originMarker)) return;
    const handleSceneAssetsComplete = () => {
      setSceneAssetsCompleteTick((tick) => tick + 1);
    };
    window.addEventListener("scene-build-assets-complete", handleSceneAssetsComplete);
    return () => {
      window.removeEventListener("scene-build-assets-complete", handleSceneAssetsComplete);
    };
  }, [meshUuid, state.boundingBox, state.originMarker]);

  useEffect(() => {
    return () => {
      renderMeshWireframeOverlayForMeshNames([], {
        visible: false,
        color: "#ff0000",
        overlayName: MESH_INSPECTOR_WIREFRAME_OVERLAY_NAME,
      });
      restoreWireframeOpacity();
      clearMeshInspectorBoundingBoxOverlays();
      const lastMesh = lastMeshUuidRef.current;
      if (lastMesh) setMeshInspectorOrientationAxes(lastMesh, false, lastBodyUuidRef.current);
      if (focusOwnedRef.current) {
        try {
          useSelectionStore.getState().clearFocusMode();
        } catch (error) {
          logger.warn("[useMeshVisibilityControls] Failed to clear focus mode on cleanup.", error);
        }
      }
    };
  }, [bodyUuid, restoreWireframeOpacity]);

  useEffect(() => {
    focusOwnedRef.current = false;
  }, [meshUuid]);

  const toggleFocus = useCallback(() => {
    if (focusDisabled) return;
    const next = !state.focus;
    if (next) {
      ensureVisualOn();
      useSelectionStore.getState().setFocusMode(true, bodyUuid, meshUuid);
    } else {
      useSelectionStore.getState().clearFocusMode();
    }
    focusOwnedRef.current = next;
    setState({ focus: next });
  }, [bodyUuid, focusDisabled, ensureVisualOn, meshUuid, setState, state.focus]);

  const toggleWireframe = useCallback(() => {
    if (disabled) return;
    const next = !state.wireframe;
    if (next) ensureVisualOn();
    setState({ wireframe: next });
  }, [disabled, ensureVisualOn, state.wireframe, setState]);

  const toggleBoundingBox = useCallback(() => {
    if (disabled) return;
    const next = !state.boundingBox;
    if (next) ensureVisualOn();
    setState({ boundingBox: next });
  }, [disabled, ensureVisualOn, state.boundingBox, setState]);

  const toggleOriginMarker = useCallback(() => {
    if (disabled) return;
    const next = !state.originMarker;
    if (next) ensureVisualOn();
    setState({ originMarker: next });
  }, [disabled, ensureVisualOn, state.originMarker, setState]);

  const setWireframeColor = useCallback(
    (hex: string) => {
      setState({ wireframeColor: hex });
    },
    [setState]
  );

  const clearAllOverlays = useCallback(() => {
    renderMeshWireframeOverlayForMeshNames([], {
      visible: false,
      color: state.wireframeColor,
      overlayName: MESH_INSPECTOR_WIREFRAME_OVERLAY_NAME,
    });
    restoreWireframeOpacity();
    clearMeshInspectorBoundingBoxOverlays();
    if (meshUuid) setMeshInspectorOrientationAxes(meshUuid, false, bodyUuid);

    setStateInternal((prev) => {
      const next = { ...prev, wireframe: false, boundingBox: false, originMarker: false };
      rememberedMeshVisibilityState = next;
      onStateChange?.(next);
      return next;
    });
  }, [bodyUuid, meshUuid, onStateChange, restoreWireframeOpacity, state.wireframeColor]);

  const reset = useCallback(() => {
    clearAllOverlays();
    if (focusOwnedRef.current) {
      try {
        useSelectionStore.getState().clearFocusMode();
      } catch (error) {
        logger.warn("[useMeshVisibilityControls] Failed to clear focus mode on reset.", error);
      }
      focusOwnedRef.current = false;
    }
    setStateInternal(() => {
      const next = { ...defaultState(), ...initialState };
      rememberedMeshVisibilityState = next;
      onStateChange?.(next);
      return next;
    });
  }, [clearAllOverlays, initialState, onStateChange]);

  return {
    state,
    disabled,
    focusDisabled,
    toggleFocus,
    toggleWireframe,
    toggleBoundingBox,
    toggleOriginMarker,
    setWireframeColor,
    setState,
    clearAllOverlays,
    reset,
  };
}
